import type {
    CallHistoryFavoriteItem,
    CallHistoryRecentItem,
} from '@pexip/media-components';

import {applicationConfig} from '../applicationConfig';
import {config} from '../config';
import {MAX_FAVORITES} from '../constants';
import {logger} from '../logger';
import {
    storageUtils as idbStorageUtils,
    type StorageUtils,
} from '../utils/indexedDB';
import {
    recentsSignal,
    favoritesSignal,
    favoritesOrderSignal,
    openFavoritesLimitReachedModalSignal,
} from '../signals/CallHistory.signals';

const CALL_HISTORY_DATABASE_NAME = 'call-history';
const CALL_HISTORY_DATABASE_VERSION = 1;
const RECENTS_STORE_NAME = 'recents';
const FAVORITES_STORE_NAME = 'favorites';
export const MAX_RECENTS = 50;

export const createCallHistoryService = (
    storageUtils: StorageUtils = idbStorageUtils,
) => {
    const recentsMap = new Map<string, CallHistoryRecentItem>();
    const favoritesMap = new Map<string, CallHistoryFavoriteItem>();

    const upgradeCallHistoryStore = (db: IDBDatabase) => {
        if (!db.objectStoreNames.contains(RECENTS_STORE_NAME)) {
            db.createObjectStore(RECENTS_STORE_NAME, {
                keyPath: 'meetingId',
            });
        }
        if (!db.objectStoreNames.contains(FAVORITES_STORE_NAME)) {
            db.createObjectStore(FAVORITES_STORE_NAME, {
                keyPath: 'meetingId',
            });
        }
    };

    const openDB = () =>
        storageUtils.open(
            CALL_HISTORY_DATABASE_NAME,
            CALL_HISTORY_DATABASE_VERSION,
            upgradeCallHistoryStore,
        );

    const persistRecent = async (item: CallHistoryRecentItem) => {
        let closeDB: (() => void) | undefined;
        try {
            const {db: database, close} = await openDB();
            closeDB = close;
            const {transaction, endTransaction} = storageUtils.beginTransaction(
                database,
                [RECENTS_STORE_NAME],
                'readwrite',
            );
            const store = transaction.objectStore(RECENTS_STORE_NAME);
            await storageUtils.request(
                store.put(item),
                `Write recent[${item.meetingId}] to DB.`,
            );
            endTransaction();
            closeDB();
        } catch (error) {
            logger.error({error}, `Error persisting recent[${item.meetingId}]`);
            closeDB?.();
        }
    };

    const deleteRecentFromDB = async (meetingId: string) => {
        let closeDB: (() => void) | undefined;
        try {
            const {db: database, close} = await openDB();
            closeDB = close;
            const {transaction, endTransaction} = storageUtils.beginTransaction(
                database,
                [RECENTS_STORE_NAME],
                'readwrite',
            );
            const store = transaction.objectStore(RECENTS_STORE_NAME);
            await storageUtils.request(
                store.delete(meetingId),
                `Delete recent[${meetingId}] from DB.`,
            );
            endTransaction();
            closeDB();
        } catch (error) {
            logger.error({error}, `Error deleting recent[${meetingId}]`);
            closeDB?.();
        }
    };

    const clearRecentsFromDB = async () => {
        let closeDB: (() => void) | undefined;
        try {
            const {db: database, close} = await openDB();
            closeDB = close;
            const {transaction, endTransaction} = storageUtils.beginTransaction(
                database,
                [RECENTS_STORE_NAME],
                'readwrite',
            );
            const store = transaction.objectStore(RECENTS_STORE_NAME);
            await storageUtils.request(
                store.clear(),
                'Clear all recents from DB.',
            );
            endTransaction();
            closeDB();
        } catch (error) {
            logger.error({error}, 'Error clearing recents from DB');
            closeDB?.();
        }
    };

    const persistFavorite = async (item: CallHistoryFavoriteItem) => {
        let closeDB: (() => void) | undefined;
        try {
            const {db: database, close} = await openDB();
            closeDB = close;
            const {transaction, endTransaction} = storageUtils.beginTransaction(
                database,
                [FAVORITES_STORE_NAME],
                'readwrite',
            );
            const store = transaction.objectStore(FAVORITES_STORE_NAME);
            await storageUtils.request(
                store.put(item),
                `Write favorite[${item.meetingId}] to DB.`,
            );
            endTransaction();
            closeDB();
        } catch (error) {
            logger.error(
                {error},
                `Error persisting favorite[${item.meetingId}]`,
            );
            closeDB?.();
        }
    };

    const deleteFavoriteFromDB = async (meetingId: string) => {
        let closeDB: (() => void) | undefined;
        try {
            const {db: database, close} = await openDB();
            closeDB = close;
            const {transaction, endTransaction} = storageUtils.beginTransaction(
                database,
                [FAVORITES_STORE_NAME],
                'readwrite',
            );
            const store = transaction.objectStore(FAVORITES_STORE_NAME);
            await storageUtils.request(
                store.delete(meetingId),
                `Delete favorite[${meetingId}] from DB.`,
            );
            endTransaction();
            closeDB();
        } catch (error) {
            logger.error({error}, `Error deleting favorite[${meetingId}]`);
            closeDB?.();
        }
    };

    const clearFavoritesFromDB = async () => {
        let closeDB: (() => void) | undefined;
        try {
            const {db: database, close} = await openDB();
            closeDB = close;
            const {transaction, endTransaction} = storageUtils.beginTransaction(
                database,
                [FAVORITES_STORE_NAME],
                'readwrite',
            );
            const store = transaction.objectStore(FAVORITES_STORE_NAME);
            await storageUtils.request(
                store.clear(),
                'Clear all favorites from DB.',
            );
            endTransaction();
            closeDB();
        } catch (error) {
            logger.error({error}, 'Error clearing favorites from DB');
            closeDB?.();
        }
    };

    const loadFromDB = async () => {
        let closeDB: (() => void) | undefined;
        try {
            const {db: database, close} = await openDB();
            closeDB = close;
            const {transaction, endTransaction} = storageUtils.beginTransaction(
                database,
                [RECENTS_STORE_NAME, FAVORITES_STORE_NAME],
                'readonly',
            );
            const recentsStore = transaction.objectStore(RECENTS_STORE_NAME);
            const favoritesStore =
                transaction.objectStore(FAVORITES_STORE_NAME);
            const recents = await storageUtils.request<CallHistoryRecentItem[]>(
                recentsStore.getAll(),
                'Load all recents from DB.',
            );
            const favorites = await storageUtils.request<
                CallHistoryFavoriteItem[]
            >(favoritesStore.getAll(), 'Load all favorites from DB.');
            endTransaction();
            closeDB();

            recentsMap.clear();
            for (const item of recents) {
                recentsMap.set(item.meetingId, item);
            }
            favoritesMap.clear();
            for (const item of favorites) {
                favoritesMap.set(item.meetingId, item);
            }

            invalidateRecentsSnapshot();
            invalidateFavoritesSnapshot();
            recentsSignal.emit();
            favoritesSignal.emit();
        } catch (error) {
            logger.error({error}, 'Error loading call history from DB');
            closeDB?.();
        }
    };

    let recentsSnapshot: CallHistoryRecentItem[] = [];
    let favoritesSnapshot: CallHistoryFavoriteItem[] = [];

    const invalidateRecentsSnapshot = () => {
        recentsSnapshot = Array.from(recentsMap.values()).sort(
            (a, b) =>
                new Date(b.dateAndTime).getTime() -
                new Date(a.dateAndTime).getTime(),
        );
    };

    const invalidateFavoritesSnapshot = () => {
        favoritesSnapshot = Array.from(favoritesMap.values());
    };

    const getRecents = (): CallHistoryRecentItem[] => {
        return recentsSnapshot;
    };

    const getFavorites = (): CallHistoryFavoriteItem[] => {
        return favoritesSnapshot;
    };

    const getFavoritesOrder = (): string[] => {
        try {
            return config.get('favoritesOrder').meetingIds;
        } catch {
            return [];
        }
    };

    const setFavoritesOrder = (order: string[]) => {
        const deduplicatedOrder = Array.from(new Set(order));
        try {
            config.set({
                key: 'favoritesOrder',
                value: {meetingIds: deduplicatedOrder},
                persist: true,
            });
            favoritesOrderSignal.emit();
        } catch {
            // Config not loaded yet (e.g. tests).
        }
    };

    /**
     * Ensures persisted favorites order includes all branding favorites.
     *
     * @example
     * initFavoritesOrder();
     */
    const initFavoritesOrder = () => {
        const favoritesOrder = getFavoritesOrder();
        const brandingFavorites = applicationConfig.callHistory.favorites ?? [];
        const brandingMeetingIds = brandingFavorites.map(fav => fav.meetingId);
        const missingBrandingIds = brandingMeetingIds.filter(
            meetingId => !favoritesOrder.includes(meetingId),
        );

        if (missingBrandingIds.length === 0) {
            return;
        }

        setFavoritesOrder([...favoritesOrder, ...missingBrandingIds]);
    };

    const addToFavoritesOrder = (meetingId: string) => {
        if (!meetingId) {
            return;
        }

        const favoritesOrder = getFavoritesOrder();
        if (favoritesOrder.includes(meetingId)) {
            return;
        }

        setFavoritesOrder([...favoritesOrder, meetingId]);
    };

    const removeFromFavoritesOrder = (meetingId: string) => {
        if (!meetingId) {
            return;
        }

        const favoritesOrder = getFavoritesOrder();
        setFavoritesOrder(
            favoritesOrder.filter(
                currentMeetingId => currentMeetingId !== meetingId,
            ),
        );
    };

    const clearUserFavoritesFromOrder = () => {
        const favoritesOrder = getFavoritesOrder();
        const brandingFavorites = applicationConfig.callHistory.favorites ?? [];
        const brandingMeetingIds = brandingFavorites.map(fav => fav.meetingId);
        const brandingMeetingIdSet = new Set(brandingMeetingIds);

        // Keep branding items in the same order they currently have in config.
        const brandingOrderFromConfig = favoritesOrder.filter(meetingId =>
            brandingMeetingIdSet.has(meetingId),
        );
        const missingBrandingIds = brandingMeetingIds.filter(
            meetingId => !brandingOrderFromConfig.includes(meetingId),
        );

        setFavoritesOrder([...brandingOrderFromConfig, ...missingBrandingIds]);
    };

    const addRecent = (item: CallHistoryRecentItem) => {
        recentsMap.set(item.meetingId, item);

        // Enforce max cap — remove oldest entries
        if (recentsMap.size > MAX_RECENTS) {
            invalidateRecentsSnapshot();
            const toRemove = recentsSnapshot.slice(MAX_RECENTS);
            for (const old of toRemove) {
                recentsMap.delete(old.meetingId);
                void deleteRecentFromDB(old.meetingId);
            }
        }

        void persistRecent(item);
        invalidateRecentsSnapshot();
        recentsSignal.emit();
    };

    const deleteRecent = (item: CallHistoryRecentItem) => {
        recentsMap.delete(item.meetingId);
        void deleteRecentFromDB(item.meetingId);
        invalidateRecentsSnapshot();
        recentsSignal.emit();
    };

    const deleteAllRecents = () => {
        recentsMap.clear();
        void clearRecentsFromDB();
        invalidateRecentsSnapshot();
        recentsSignal.emit();
    };

    const addFavorite = (item: CallHistoryFavoriteItem) => {
        if (favoritesMap.has(item.meetingId)) {
            return;
        }

        const brandingFavorites = applicationConfig.callHistory.favorites ?? [];
        const totalFavorites = new Set([
            ...brandingFavorites.map(favorite => favorite.meetingId),
            ...getFavorites().map(favorite => favorite.meetingId),
            item.meetingId,
        ]).size;

        if (totalFavorites > MAX_FAVORITES) {
            openFavoritesLimitReachedModalSignal.emit();
            return;
        }

        favoritesMap.set(item.meetingId, item);
        void persistFavorite(item);
        addToFavoritesOrder(item.meetingId);
        invalidateFavoritesSnapshot();
        favoritesSignal.emit();
    };

    const removeFavorite = (item: CallHistoryFavoriteItem) => {
        if (!favoritesMap.has(item.meetingId)) {
            return;
        }
        favoritesMap.delete(item.meetingId);
        void deleteFavoriteFromDB(item.meetingId);
        removeFromFavoritesOrder(item.meetingId);
        invalidateFavoritesSnapshot();
        favoritesSignal.emit();
    };

    const deleteAllFavorites = () => {
        favoritesMap.clear();
        void clearFavoritesFromDB();
        clearUserFavoritesFromOrder();
        invalidateFavoritesSnapshot();
        favoritesSignal.emit();
    };

    const reorderFavorite = (meetingId: string, newIndex: number) => {
        const favoritesOrder = getFavoritesOrder();
        const fromIndex = favoritesOrder.indexOf(meetingId);
        const orderWithoutItem = favoritesOrder.filter(id => id !== meetingId);
        const clampedIndex = Math.max(
            0,
            Math.min(newIndex, orderWithoutItem.length),
        );
        if (fromIndex !== -1 && clampedIndex === fromIndex) {
            return;
        }

        setFavoritesOrder([
            ...orderWithoutItem.slice(0, clampedIndex),
            meetingId,
            ...orderWithoutItem.slice(clampedIndex),
        ]);
    };

    const isFavorite = (meetingId: string) => {
        return favoritesMap.has(meetingId);
    };

    // Load persisted data on creation
    void loadFromDB();

    return {
        getRecents,
        getFavorites,
        getFavoritesOrder,
        initFavoritesOrder,
        addRecent,
        deleteRecent,
        deleteAllRecents,
        addFavorite,
        removeFavorite,
        deleteAllFavorites,
        reorderFavorite,
        isFavorite,
    };
};

export const callHistoryService = createCallHistoryService();

export type CallHistoryService = ReturnType<typeof createCallHistoryService>;
